/**
 * Derives Sales Price from Purchase Price using the tenant's configured profit
 * markup (Admin -> Settings -> Item Defaults).
 *
 * Shared by every screen that creates an item so the number an operator sees
 * pre-filled can never disagree with the preview shown on the settings page:
 * AddItemModal, AddItemToBranchModal's "Create New" tab, and the legacy
 * admin/ portal's copy.
 */

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Markup on cost: `sales = purchase * (1 + profitPercent / 100)`.
 * 30% on 100 -> 130.
 *
 * Both sides are the TAX-EXCLUSIVE base. BeforeAfterTaxPriceField already
 * persists only the exclusive base regardless of which box was typed into, so
 * the markup never compounds with GST.
 *
 * Returns undefined only when there is no usable purchase price. A purchase
 * price of 0 yields 0, NOT undefined — a truthiness check here would repeat
 * the existing itemService bug where `purchasePrice: 0` persists as null.
 */
export function computeSalesPriceFromPurchase(
  purchaseBase: number | undefined | null,
  profitPercent: number | undefined | null
): number | undefined {
  if (purchaseBase === undefined || purchaseBase === null) return undefined;
  if (!Number.isFinite(purchaseBase) || purchaseBase < 0) return undefined;

  const percent =
    profitPercent !== undefined && profitPercent !== null && Number.isFinite(profitPercent)
      ? profitPercent
      : 0;

  return round2(purchaseBase * (1 + percent / 100));
}
